import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, CalendarDays, Flag } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ShareStoryDialog } from "../ShareStoryDialog";
import { api, errMessage } from "../../lib/api";

export const WeekAndStories = ({ child }) => {
  const [data, setData] = useState(null);
  const [stories, setStories] = useState(null);

  const load = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get(`/children/${child.child_id}/weekly-summary`),
        api.get(`/story/list/${child.child_id}`),
      ]);
      setData(s.data);
      setStories(st.data);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const report = async (storyId) => {
    try {
      await api.post(`/story/${storyId}/report`, { reason: "Reported by parent from dashboard" });
      toast.success("Thank you. This story has been flagged for review.");
      setStories((prev) => prev.map((s) => (s.story_id === storyId ? { ...s, status: "reported" } : s)));
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <details className="rounded-xl border border-border" onToggle={(e) => e.target.open && !data && load()}>
      <summary className="cursor-pointer p-4 text-sm font-semibold flex items-center gap-2 list-none" data-testid="child-card-week-summary">
        <CalendarDays className="h-4 w-4 text-secondary" aria-hidden="true" /> This week & stories
      </summary>
      <div className="p-4 pt-0 space-y-4">
        {!data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !data.available ? (
          <p className="text-sm text-muted-foreground" data-testid="weekly-summary-no-consent">
            Turn on Progress tracking consent to see a weekly recap.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2 text-sm" data-testid="weekly-summary-stats">
            <div className="rounded-lg bg-accent p-3"><span className="font-bold text-lg block">{data.sessions}</span> play sessions</div>
            <div className="rounded-lg bg-accent p-3"><span className="font-bold text-lg block">{data.activities_completed}</span> games finished</div>
            <div className="rounded-lg bg-accent p-3"><span className="font-bold text-lg block">{data.stories_read}</span> stories enjoyed</div>
            <div className="rounded-lg bg-accent p-3"><span className="font-bold text-lg block">{data.activities_replayed}</span> favorite replays</div>
          </div>
        )}
        {data?.available && data.activities_completed + data.stories_read > 0 && (
          <p className="text-sm text-secondary font-semibold">
            {child.display_name} kept practicing this week — worth celebrating together!
          </p>
        )}
        {stories?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-secondary" aria-hidden="true" /> Recent stories
            </p>
            {stories.slice(0, 5).map((s) => (
              <div key={s.story_id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid={`story-row-${s.story_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.theme} · {s.source === "ai" ? `AI (${s.model_name || "model"})` : "Pre-authored"} · {new Date(s.created_at).toLocaleDateString()}
                  </p>
                </div>
                {s.status === "reported" ? (
                  <Badge variant="destructive" className="shrink-0">Reported</Badge>
                ) : (
                  <div className="flex items-center shrink-0">
                    <ShareStoryDialog story={s} />
                    <Button variant="ghost" size="sm" className="shrink-0 gap-1 text-muted-foreground h-8"
                      onClick={() => report(s.story_id)} data-testid={`report-story-${s.story_id}`}>
                      <Flag className="h-3.5 w-3.5" aria-hidden="true" /> Report
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  );
};
